import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const ConfirmModal = ({ show, title, message, onConfirm, onCancel }) => {
  if (!show) return null;
  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }} onClick={onCancel} />
      <div role="dialog" aria-modal="true" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1055, width: "100%", maxWidth: 420, background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", padding: "28px 28px 22px" }}>
        <div className="d-flex align-items-center gap-3 mb-3">
          <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff0f0" }}>
            <i className="bi bi-trash3" style={{ fontSize: 20, color: "#dc3545" }} />
          </div>
          <h6 className="mb-0 fw-bold" style={{ fontSize: 16 }}>{title}</h6>
        </div>
        <p className="text-muted mb-4" style={{ fontSize: 14, lineHeight: 1.6 }}>{message}</p>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-sm btn-danger px-4" style={{ borderRadius: 8 }} onClick={onConfirm}>Delete</button>
          <button className="btn btn-sm btn-outline-secondary px-4" style={{ borderRadius: 8 }} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </>
  );
};

const VENDOR_TYPES = ["Individual", "Company", "Government", "NGO", "Other"];
const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const PAYMENT_TERMS = ["Net 15", "Net 30", "Net 45", "Due on receipt"];
const STATUSES = ["Active", "Inactive", "Blacklisted"];
const ADDRESS_TYPES = ["Billing", "Shipping", "Office", "Warehouse", "Other"];
const ACCOUNT_TYPES = ["Savings", "Current", "Overdraft", "Cash Credit"];

const emptyAddress = () => ({ addressType: "Billing", addressLine1: "", addressLine2: "", city: "", state: "", country: "", postalCode: "" });

const emptyForm = {
  name: "",
  contactPerson: "",
  vendorType: "Company",
  industry: "",
  website: "",
  phone: "",
  email: "",
  gstNumber: "",
  status: "Active",
  currency: "INR",
  paymentTerms: "Net 30",
  bankName: "",
  accountHolderName: "",
  accountNumber: "",
  ifscCode: "",
  swiftCode: "",
  branchName: "",
  accountType: "",
  addresses: [emptyAddress()],
};

export default function EditVendor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const refs = useRef({});

  const [activeTab, setActiveTab] = useState("addresses");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["addresses"]));
  const [navbarColor, setNavbarColor] = useState("#080235");

  const setRef = (name) => (element) => { refs.current[name] = element; };

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    if (!token) { toast.error("You must be logged in."); navigate("/"); }
  }, [navigate, token]);

  useEffect(() => {
      const userId = localStorage.getItem("userId");
      if (!token || !userId) return;
      fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  useEffect(() => {
    const loadVendor = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error("Failed to load vendor.");
        const data = await res.json();
        setFormData({
          ...emptyForm,
          ...data,
          addresses: Array.isArray(data.addresses) && data.addresses.length > 0 ? data.addresses : [emptyAddress()],
        });
      } catch (err) {
        toast.error(err.message || "Failed to load vendor.");
        navigate("/vendors");
      } finally {
        setLoading(false);
      }
    };
    if (token && id) loadVendor();
  }, [id, navigate, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleAddressChange = (idx, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.addresses];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, addresses: updated };
    });
  };

  const addAddress = () => setFormData((prev) => ({ ...prev, addresses: [...prev.addresses, emptyAddress()] }));
  const removeAddress = (idx) => setFormData((prev) => ({ ...prev, addresses: prev.addresses.filter((_, i) => i !== idx) }));

  const inputClass = (field) => {
    if (errors[field]) return "form-control is-invalid";
    if (touched[field]) return "form-control is-valid";
    return "form-control";
  };

  const selectClass = (field) => {
    if (errors[field]) return "form-select is-invalid";
    if (touched[field]) return "form-select is-valid";
    return "form-select";
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Vendor name is required.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Enter a valid email.";
    if (formData.phone && !/^[0-9+\-() ]{7,20}$/.test(formData.phone)) e.phone = "Enter a valid phone number.";
    if (formData.gstNumber && formData.gstNumber.length < 8) e.gstNumber = "GST number looks too short.";
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) e.website = "Website must start with http:// or https://";
    return e;
  };

  const focusFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    const el = refs.current[firstKey];
    if (el?.focus) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  };

  const handleSubmit = async () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    setTouched(Object.fromEntries(Object.keys(emptyForm).map(k => [k, true])));

    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(nextErrors);
      toast.error("Please fix the form errors before saving.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/vendors/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Vendor updated successfully.");
      setTimeout(() => navigate(`/viewvendor/${id}`), 1500);
    } catch (err) {
      toast.error(err.message || "Failed to update vendor.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setConfirmOpen(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Unable to delete vendor.");
      toast.success("Vendor deleted successfully.");
      navigate("/vendors");
    } catch (err) {
      toast.error(err.message || "Failed to delete vendor.");
    }
  };

  if (loading) {
    return (
      <>
        <div className="fixed-top"><AssetModuleNavbar /></div>
        <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>Loading...</div>
      </>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="mb-3">
          <h4 className="m-0"><i className="bi bi-pencil-square me-2"></i> Edit Vendor</h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button type="button" className="btn btn-primary me-2" disabled={submitting} onClick={handleSubmit}>
                {submitting ? "Saving..." : "Save"}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
            </div>
            <div className="d-flex gap-3 me-3">
              <a href={`/viewvendor/${id}`} className="text-primary text-decoration-none">View</a>
              <a href="/vendors" className="text-primary text-decoration-none">List</a>
            </div>
          </div>
        </div>

        <form>
          <Section title="Basic Information">
            <div className="col-md-4">
              <label className="form-label">Vendor Name <span className="text-danger">*</span></label>
              <input ref={setRef("name")} type="text" name="name" className={inputClass("name")} value={formData.name} onChange={handleChange} />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Contact Person</label>
              <input ref={setRef("contactPerson")} type="text" name="contactPerson" className={inputClass("contactPerson")} value={formData.contactPerson} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Vendor Type</label>
              <select name="vendorType" className={selectClass("vendorType")} value={formData.vendorType || ""} onChange={handleChange}>
                <option value="">Select type…</option>
                {VENDOR_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Industry</label>
              <select name="industry" className="form-select" value={formData.industry || ""} onChange={handleChange}>
                <option value="">Select industry…</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Website</label>
              <input ref={setRef("website")} type="url" name="website" className={inputClass("website")} value={formData.website || ""} onChange={handleChange} placeholder="https://example.com" />
              {errors.website && <div className="invalid-feedback">{errors.website}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">GST Number</label>
              <input ref={setRef("gstNumber")} type="text" name="gstNumber" className={inputClass("gstNumber")} value={formData.gstNumber || ""} onChange={handleChange} />
              {errors.gstNumber && <div className="invalid-feedback">{errors.gstNumber}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Status</label>
              <select name="status" className={selectClass("status")} value={formData.status || "Active"} onChange={handleChange}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </Section>

          <Section title="Contact Details">
            <div className="col-md-4">
              <label className="form-label">Phone</label>
              <input ref={setRef("phone")} type="text" name="phone" className={inputClass("phone")} value={formData.phone || ""} onChange={handleChange} />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Email</label>
              <input ref={setRef("email")} type="email" name="email" className={inputClass("email")} value={formData.email || ""} onChange={handleChange} />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </Section>

        </form>

        <ul className="nav nav-tabs mb-0 fw-semibold" style={{ backgroundColor: navbarColor }}>
          {[["addresses", "Address Details"], ["financial", "Financial"], ["systemNotes", "System Notes"]].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(key);
                  setVisitedTabs(prev => new Set(prev).add(key));
                }}
                style={{
                  color: activeTab === key ? navbarColor : "#fff",
                  backgroundColor: activeTab === key ? "#fff" : "transparent",
                  fontWeight: activeTab === key ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <div className="border p-3 mb-3">
          {activeTab === "addresses" && (
            <Section title="Address Details">
              {(formData.addresses || []).map((addr, idx) => (
                <div key={idx} className="col-12 border rounded p-0 mb-2 overflow-hidden">
                  <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-secondary">{addr.addressType || "Billing"}</span>
                      <span className="text-muted small">Address {idx + 1}</span>
                    </div>
                    {formData.addresses.length > 1 && (
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeAddress(idx)}>Remove</button>
                    )}
                  </div>
                  <div className="row g-2 p-3">
                    <div className="col-md-3">
                      <label className="form-label">Address Type</label>
                      <select className="form-select" value={addr.addressType || "Billing"} onChange={(e) => handleAddressChange(idx, "addressType", e.target.value)}>
                        {ADDRESS_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="col-md-9">
                      <label className="form-label">Address Line 1</label>
                      <input type="text" className="form-control" value={addr.addressLine1 || ""} onChange={(e) => handleAddressChange(idx, "addressLine1", e.target.value)} placeholder="Street address" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Address Line 2</label>
                      <input type="text" className="form-control" value={addr.addressLine2 || ""} onChange={(e) => handleAddressChange(idx, "addressLine2", e.target.value)} placeholder="Apartment, suite, building (optional)" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">City</label>
                      <input type="text" className="form-control" value={addr.city || ""} onChange={(e) => handleAddressChange(idx, "city", e.target.value)} placeholder="City" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">State / Province</label>
                      <input type="text" className="form-control" value={addr.state || ""} onChange={(e) => handleAddressChange(idx, "state", e.target.value)} placeholder="State" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Country</label>
                      <input type="text" className="form-control" value={addr.country || ""} onChange={(e) => handleAddressChange(idx, "country", e.target.value)} placeholder="Country" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Postal Code</label>
                      <input type="text" className="form-control" value={addr.postalCode || ""} onChange={(e) => handleAddressChange(idx, "postalCode", e.target.value)} placeholder="PIN / ZIP" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-12">
                <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={addAddress}>+ Add Address</button>
              </div>
            </Section>
          )}
          {activeTab === "financial" && (
            <Section title="Financial">
              <div className="col-md-4">
                <label className="form-label">Currency</label>
                <select name="currency" className={selectClass("currency")} value={formData.currency || "INR"} onChange={handleChange}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Payment Terms</label>
                <select name="paymentTerms" className="form-select" value={formData.paymentTerms || ""} onChange={handleChange}>
                  <option value="">Select…</option>
                  {PAYMENT_TERMS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="col-12"><hr className="my-1" /><strong className="text-muted" style={{ fontSize: 13 }}>Bank / Payment Details</strong></div>
              <div className="col-md-4">
                <label className="form-label">Bank Name</label>
                <input type="text" name="bankName" className="form-control" value={formData.bankName || ""} onChange={handleChange} placeholder="e.g. State Bank of India" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Account Holder Name</label>
                <input type="text" name="accountHolderName" className="form-control" value={formData.accountHolderName || ""} onChange={handleChange} placeholder="Name on account" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Account Type</label>
                <select name="accountType" className="form-select" value={formData.accountType || ""} onChange={handleChange}>
                  <option value="">Select…</option>
                  {ACCOUNT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Account Number</label>
                <input type="text" name="accountNumber" className="form-control" value={formData.accountNumber || ""} onChange={handleChange} placeholder="Bank account number" />
              </div>
              <div className="col-md-4">
                <label className="form-label">IFSC Code</label>
                <input type="text" name="ifscCode" className="form-control" value={formData.ifscCode || ""} onChange={handleChange} placeholder="e.g. SBIN0001234" />
              </div>
              <div className="col-md-4">
                <label className="form-label">SWIFT / BIC Code</label>
                <input type="text" name="swiftCode" className="form-control" value={formData.swiftCode || ""} onChange={handleChange} placeholder="For international transfers" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Branch Name</label>
                <input type="text" name="branchName" className="form-control" value={formData.branchName || ""} onChange={handleChange} placeholder="Branch name or address" />
              </div>
            </Section>
          )}
          {activeTab === "systemNotes" && (
            <>
              <div className="text-muted small mb-2">
                <strong>Date Created:</strong>{" "}
                {formData?.createdAt ? new Date(formData.createdAt).toLocaleString() : "—"}
              </div>
              {visitedTabs.has("systemNotes") && (
                <SystemNotes entityType="VENDOR" entityId={id} token={token} />
              )}
            </>
          )}
        </div>

        <div>
          <button type="button" className="btn btn-primary me-2" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "Saving..." : "Save"}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </div>

      <ConfirmModal
        show={confirmOpen}
        title="Delete Vendor"
        message={`Are you sure you want to delete "${formData.name || "this vendor"}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
